import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { ChatWidgetProps, Message } from "../../types";
import { useSessionId } from "../../hooks/useSessionId";
import { useMessages } from "../../hooks/useMessages";
import { useSendMessage } from "../../hooks/useSendMessage";
import { useTheme as useThemeHook } from "../../hooks/useTheme";
import { useI18n } from "../../hooks/useI18n";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import { Header } from "./Header";
import { WelcomeScreen } from "./WelcomeScreen";
import { WidgetToggle } from "./WidgetToggle";

/**
 * Componente principal do Chat Widget
 */
export const ChatWidget: React.FC<ChatWidgetProps> = ({
  // URLs obrigatórias
  loadMessagesUrl,
  sendMessageUrl,

  // Configurações
  authHeader,
  sessionId: fixedSessionId,
  generateSessionId,
  loadPreviousSession = false,
  refetchInterval = 3000,
  enablePolling = true,

  // Conteúdo
  initialMessages = [],
  icon,

  // Internacionalização
  i18n,
  defaultLanguage = "en",

  // Welcome Screen e Mode
  showWelcomeScreen = false,
  mode = "window",

  // Estilos e Tema
  customStyles = {},
  theme,
  enableMarkdown = true,
  showTimestamps = true,

  // Callbacks
  onMessageSent,
  onMessagesLoaded,
  onError,
  onBlockStateChange,
}) => {
  // Processa tema e gera estilos
  const { theme: processedTheme, styles } = useThemeHook(theme, customStyles);

  // Internacionalização
  const translations = useI18n(i18n, defaultLanguage);

  // Estabiliza initialMessages para evitar re-renders desnecessários
  const initialMessagesKey = useMemo(
    () => JSON.stringify(translations.initialMessages),
    [translations.initialMessages]
  );

  // Estabiliza a prop initialMessages para evitar re-renders
  const stableInitialMessages = useMemo(
    () => initialMessages || [],
    [JSON.stringify(initialMessages)]
  );

  // Cria mensagens iniciais do bot de forma estável (memoizada)
  const botInitMessages = useMemo(() => {
    const messagesToAdd = translations.initialMessages || [];
    if (messagesToAdd.length === 0) return [];

    const fixedTimestamp = new Date().toISOString();
    return messagesToAdd.map((msgContent: string, index: number) => ({
      id: `initial-message-${index}`,
      message: msgContent,
      sender: "system" as const,
      timestamp: fixedTimestamp,
      type: 'text' as const,
    }));
  }, [translations.initialMessages]);

  // Estados
  const [displayMessages, setDisplayMessages] = useState<Message[]>(
    stableInitialMessages
  );
  const [showError, setShowError] = useState(false);
  const [isWelcomeScreenVisible, setIsWelcomeScreenVisible] =
    useState(showWelcomeScreen);
  const [isOpen, setIsOpen] = useState(mode === "fullscreen");

  // Hook de sessão (prioridade: fixedSessionId > generateSessionId > auto-gerado)
  const { sessionId } = useSessionId(
    fixedSessionId,
    generateSessionId,
    loadPreviousSession
  );

  // Hook de carregamento de mensagens
  const {
    messages: loadedMessages,
    isLoading,
    isBlocked,
    error: messagesError,
    loadMessages,
    addMessage,
    setBlockState,
    resetPolling,
  } = useMessages({
    loadMessagesUrl,
    sessionId,
    authConfig: authHeader,
    refetchInterval,
    enablePolling,
    onMessagesLoaded,
    onError,
    onBlockStateChange,
  });

  // Hook de envio de mensagens
  const {
    sendMessage,
    isSending,
    error: sendError,
  } = useSendMessage({
    sendMessageUrl,
    sessionId,
    authConfig: authHeader,
    onMessageSent,
    onError,
  });

  // Atualiza mensagens exibidas
  useEffect(() => {
    console.log(
      "[ChatWidget] loadedMessages mudou:",
      loadedMessages.length,
      "mensagens"
    );

    // Combina mensagens iniciais + mensagens carregadas do backend
    // Initial messages sempre aparecem primeiro
    if (loadedMessages.length > 0) {
      console.log(
        "[ChatWidget] Atualizando displayMessages com initial messages + loaded messages"
      );
      setDisplayMessages([...botInitMessages, ...loadedMessages]);
    } else if (stableInitialMessages.length > 0) {
      console.log(
        "[ChatWidget] Usando initialMessages:",
        stableInitialMessages.length
      );
      setDisplayMessages(stableInitialMessages);
    } else if (botInitMessages.length > 0) {
      console.log("[ChatWidget] Usando apenas bot initial messages");
      setDisplayMessages(botInitMessages);
    }
  }, [loadedMessages, stableInitialMessages, botInitMessages]);

  // Gerencia exibição de erros
  useEffect(() => {
    const error = messagesError || sendError;
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => setShowError(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [messagesError, sendError]);

  /**
   * Lida com envio de mensagem
   */
  const handleSendMessage = async (content: string) => {
    // Cria mensagem temporária (otimista)
    const tempMessage: Message = {
      id: `temp-${Date.now()}`,
      message: content,
      timestamp: new Date().toISOString(),
      sender: "customer",
      type: 'text' as const,
    };

    // Adiciona mensagem temporária à UI
    addMessage(tempMessage);

    try {
      await sendMessage(content);

      // Se chegou aqui = 200 OK, bloqueia input e reseta polling
      console.log("[ChatWidget] Mensagem enviada com sucesso, bloqueando input e resetando polling");
      setBlockState(true);
      resetPolling(); // Reseta o contador do polling
    } catch (error) {
      // Em caso de erro, a mensagem temporária será substituída pela real no próximo fetch
      console.error("Erro ao enviar mensagem:", error);
    }
  };

  /**
   * Handler para quando usuário clica em "Get Started" na welcome screen
   */
  const handleGetStarted = () => {
    setIsWelcomeScreenVisible(false);
  };

  /**
   * Handler para abrir/fechar widget (mode: window)
   */
  const handleToggleWidget = () => {
    setIsOpen((prev) => !prev);
  };

  const currentError = messagesError || sendError;

  // Se welcome screen está visível, renderiza ela ao invés do chat
  if (isWelcomeScreenVisible) {
    return (
      <div style={styles.container}>
        <WelcomeScreen
          translations={translations}
          onGetStarted={handleGetStarted}
          themeStyles={{
            backgroundColor: processedTheme.colors?.lightBackground,
            primaryColor: processedTheme.colors?.headerBackground,
            textColor: processedTheme.colors?.messageBotText,
          }}
        />
      </div>
    );
  }

  // Estilos com transição para mode window (cresce/diminui do botão)
  const windowContainerWithTransition =
    mode === "window"
      ? {
          ...styles.windowContainer,
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "scale(1)" : "scale(0)",
          pointerEvents: isOpen ? ("auto" as const) : ("none" as const),
          transition:
            "transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease",
          transformOrigin: "bottom right",
        }
      : styles.container;

  // Renderiza o conteúdo do chat
  const chatContent = (
    <>
      <div style={windowContainerWithTransition}>
        {/* Header com botão de fechar em mode window */}
        <Header
          title={translations.title}
          subtitle={translations.subtitle}
          icon={icon}
          containerStyle={styles.header}
          titleStyle={styles.headerTitle}
          subtitleStyle={styles.headerSubtitle}
          iconStyle={styles.headerIcon}
          onClose={mode === "window" ? handleToggleWidget : undefined}
        />

        {/* Mensagem de erro */}
        {showError && currentError && (
          <div style={styles.errorMessage}>{currentError.message}</div>
        )}

        {/* Lista de mensagens */}
        <MessageList
          messages={displayMessages}
          isLoading={isLoading}
          enableMarkdown={enableMarkdown}
          showTimestamps={showTimestamps}
          customStyles={{
            container: styles.messagesContainer,
            messageUser: styles.messageUser,
            messageBot: styles.messageBot,
            messageContent: styles.messageContent,
            messageTimestamp: styles.messageTimestamp,
            loadingIndicator: styles.loadingIndicator,
          }}
        />

        {/* Input de mensagem */}
        <MessageInput
          onSend={handleSendMessage}
          isDisabled={isBlocked}
          isSending={isSending}
          placeholder={translations.inputPlaceholder}
          customStyles={{
            container: styles.inputContainer,
            input: styles.input,
            sendButton: styles.sendButton,
            sendButtonDisabled: styles.sendButtonDisabled,
          }}
        />
      </div>
    </>
  );

  // Mode window: renderiza toggle + chat sempre no DOM
  if (mode === "window") {
    return (
      <>
        <WidgetToggle
          onClick={handleToggleWidget}
          icon={processedTheme.general?.widgetIcon}
          customStyles={styles.toggleButton}
        />
        {chatContent}
      </>
    );
  }

  // Mode fullscreen: renderiza sempre o chat
  return chatContent;
};

export default ChatWidget;
